import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import type {
  ElementType,
  ForwardedRef,
  ReactNode,
  TextareaHTMLAttributes,
} from "react";
import { forwardRef } from "react";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  fullWidth?: boolean;
  name?: string;
  radius?: "none" | "rounded";
  dense?: boolean;
  textareaSize?: "md" | "lg";
  label?: string;
  WrapperComponent?: ElementType;
  resizable?: "none" | "horizontal" | "vertical" | "both";
  classNames?: {
    wrapper?: string;
    label?: string;
    textarea?: string;
    hint?: string;
  };
  hint?: string;
  HintComponent?: ElementType;
  hintId?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
}

const textareaStyle = cva(
  "action-focus w-full border bg-transparent align-top",
  {
    variants: {
      radius: {
        none: "rounded-none",
        rounded: "rounded-lg",
      },
      dense: {
        true: "p-2",
        false: "p-4",
      },
      size: {
        md: " text-lg",
        lg: "text-lg",
      },
      resizable: {
        none: "resize-none",
        horizontal: "resize-x",
        vertical: "resize-y",
        both: "resize",
      },
    },
  },
);

export const Textarea = forwardRef(
  (
    {
      name,
      fullWidth = false,
      textareaSize = "md",
      dense = false,
      label = "",
      classNames,
      radius = "none",
      WrapperComponent = "div",
      rows = 4,
      resizable = "vertical",
      hint,
      HintComponent = "div",
      hintId,
      startAdornment = null,
      endAdornment = null,
      ...props
    }: TextareaProps,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => (
    <WrapperComponent
      className={cn("inline-block", fullWidth && "w-full", classNames?.wrapper)}
    >
      {label && (
        <label
          className={cn("mb-1 block text-base", classNames?.label)}
          htmlFor={name}
        >
          {label} {props.required && "*"}
        </label>
      )}
      <div className="relative w-full">
        {startAdornment}
        <textarea
          ref={ref}
          name={name}
          className={cn(
            textareaStyle({ radius, dense, size: textareaSize, resizable }),
            props.disabled && "opacity-50",
            classNames?.textarea,
          )}
          rows={rows}
          aria-describedby={hintId}
          {...props}
        />
        {endAdornment}
      </div>

      {hint && (
        <HintComponent
          id={hintId}
          className={cn("mt-1 text-sm opacity-75", classNames?.hint)}
        >
          {hint}
        </HintComponent>
      )}
    </WrapperComponent>
  ),
);
